import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import PaystubViewer from './components/PaystubViewer';
// import PaystubList from './components/PaystubList';
import PaystubPeriodList from './components/PaystubPeriodList';
// import AttendanceCard from './components/AttendanceCard'; // Replaced by CurrentPayrollConfirmation
import AdminDashboard from './components/AdminDashboard';
import CurrentPayrollConfirmation from './components/CurrentPayrollConfirmation';
import PayRateCard from './components/PayRateCard';
import RateAdjustmentModal from './components/RateAdjustmentModal';
import MyRateRequests from './components/MyRateRequests';
import { testSupabaseConnection } from './utils/testConnection';
import './App.css';

// Cache busting version - update this timestamp when deploying new code
const APP_VERSION = '2025-01-27-16:00:00';
const VERSION_KEY = 'paystub_app_version';

// Utility function to clear all caches and force refresh
const clearAllCaches = () => {
  console.log('Clearing all caches and forcing refresh...');
  
  // Clear localStorage and sessionStorage
  localStorage.clear();
  sessionStorage.clear();
  
  // Clear service worker caches
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    });
  }
  
  // Clear IndexedDB if available
  if ('indexedDB' in window) {
    try {
      indexedDB.databases().then(databases => {
        databases.forEach(db => {
          indexedDB.deleteDatabase(db.name);
        });
      });
    } catch (e) {
      console.log('IndexedDB clear failed:', e);
    }
  }
  
  // Force hard refresh with cache bypass
  window.location.reload(true);
};

function App() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [paystubs, setPaystubs] = useState([]);
  const [selectedPaystub, setSelectedPaystub] = useState(null);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [currentPayrollNumber, setCurrentPayrollNumber] = useState(null);
  const [impersonationMode, setImpersonationMode] = useState(null);
  const [adminSession, setAdminSession] = useState(null);
  const [showRateAdjustmentModal, setShowRateAdjustmentModal] = useState(false);
  const [currentRates, setCurrentRates] = useState([]);
  const [rejectedRequest, setRejectedRequest] = useState(null);

  useEffect(() => {
    // Check for app version changes and clear cache if needed
    const checkAppVersion = () => {
      const storedVersion = localStorage.getItem(VERSION_KEY);
      if (storedVersion !== APP_VERSION) {
        console.log('App version changed, clearing cache...', { storedVersion, currentVersion: APP_VERSION });
        
        // Store new version first
        localStorage.setItem(VERSION_KEY, APP_VERSION);
        
        // Clear session storage and auth data only (less aggressive)
        sessionStorage.clear();
        const authKeys = Object.keys(localStorage).filter(key => key.includes('supabase') || key.includes('auth'));
        authKeys.forEach(key => {
          localStorage.removeItem(key);
        });
        
        // Don't force reload immediately - let the app continue
        return false; // No immediate reload
      }
      return false; // No cache clearing needed
    };

    // Test connection and check for existing session on app start
    const initializeApp = async () => {
      // Check version first
      const cacheCleared = checkAppVersion();
      if (cacheCleared) {
        return; // App will reload, so exit early
      }

      // Test connection first
      const result = await testSupabaseConnection();
      if (result.success) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('error');
        setError(`Connection failed: ${result.error}`);
        return;
      }

      // Check for existing session
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (session) {
        // Get user phone from session
        const userPhone = session.user.phone;

        // Check if this is the admin user
        const adminPhone = '+19299906651';
        // Normalize phone numbers for comparison (add + if missing)
        const normalizedUserPhone = userPhone.startsWith('+') ? userPhone : `+${userPhone}`;
        const isAdminUser = normalizedUserPhone === adminPhone;
        setIsAdmin(isAdminUser);
        
        if (isAdminUser) {
          // Admin user - just authenticate
          setIsAuthenticated(true);
          setPhoneNumber(userPhone);
        } else {
          // Regular user - get employee data and paystubs
          try {
            // Try with normalized phone number
            let { data: employeeData, error: employeeError } = await supabase
              .from('employees')
              .select('employee_number, payroll_name, employee_id_kf, employee_id_koo, phone, email')
              .eq('phone', normalizedUserPhone);

            // If first query fails, try with different phone formats
            if (employeeError || !employeeData || employeeData.length === 0) {
              // Try without + prefix
              const phoneWithoutPlus = normalizedUserPhone.replace('+', '');
              const { data: altData1, error: altError1 } = await supabase
                .from('employees')
                .select('employee_number, payroll_name, employee_id_kf, employee_id_koo, phone, email')
                .eq('phone', phoneWithoutPlus);
              
              if (!altError1 && altData1 && altData1.length > 0) {
                employeeData = altData1;
                employeeError = null;
              } else {
                // Try with 1 prefix
                const phoneWithOne = phoneWithoutPlus.startsWith('1') ? phoneWithoutPlus : `1${phoneWithoutPlus}`;
                const { data: altData2, error: altError2 } = await supabase
                  .from('employees')
                  .select('employee_number, payroll_name, employee_id_kf, employee_id_koo, phone, email')
                  .eq('phone', phoneWithOne);
                
                if (!altError2 && altData2 && altData2.length > 0) {
                  employeeData = altData2;
                  employeeError = null;
                }
              }
            }

            if (employeeError || !employeeData || employeeData.length === 0) {
              console.warn('Employee not found for authenticated user');
              await supabase.auth.signOut();
              return;
            }

            const employee = employeeData[0];
            setCurrentEmployee(employee);
            setPhoneNumber(userPhone);
            
            // Fetch paystubs for authenticated employee - only published periods
            // First get published payroll periods
            const { data: publishedPeriods, error: periodsError } = await supabase
              .from('payroll_period')
              .select('payroll_number')
              .eq('status', 'Published')
              .order('payroll_number', { ascending: false });

            if (periodsError) {
              console.error('Error fetching published periods:', periodsError);
              return;
            }

            if (publishedPeriods && publishedPeriods.length > 0) {
              const publishedPayrollNumbers = publishedPeriods.map(p => p.payroll_number);
              
              const { data: allPaystubData, error: fetchError } = await supabase
                .from('payroll_summary')
                .select('*')
                .eq('employee_number', employee.employee_number)
                .in('payroll_number', publishedPayrollNumbers) // Only published periods
                .order('payroll_number', { ascending: false });

              if (fetchError) {
                console.error('Error fetching paystubs:', fetchError);
              } else {
                setPaystubs(allPaystubData || []);
              }
            }
            
            setIsAuthenticated(true);
          } catch (error) {
            console.error('Error restoring session:', error);
            await supabase.auth.signOut();
          }
        }
      }
    };
    
    initializeApp();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setIsAdmin(false);
        setCurrentEmployee(null);
        setPaystubs([]);
        setSelectedPaystub(null);
        setPhoneNumber('');
        setCode('');
        setVerificationSent(false);
        setImpersonationMode(null);
        setAdminSession(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);


  const handleSignIn = async () => {
    if (connectionStatus !== 'connected') {
      setError('Database connection unavailable. Please try again later.');
      return;
    }

    try {
      setError('');
      
      // Clear session storage on login to ensure fresh state
      sessionStorage.clear();
      
      // Remove all non-digits
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      
      if (!cleanPhone || cleanPhone.length < 10) {
        throw new Error('Please enter a valid phone number');
      }
      
      // Add +1 if it's not already there
      let formattedPhoneNumber;
      if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
        formattedPhoneNumber = `+${cleanPhone}`;
      } else if (cleanPhone.length === 10) {
        formattedPhoneNumber = `+1${cleanPhone}`;
      } else {
        formattedPhoneNumber = `+${cleanPhone}`;
      }
      
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhoneNumber,
      });

      if (error) {
        console.error('Sign in error:', error);
        if (error.message.includes('service_unavailable')) {
          throw new Error('SMS service is temporarily unavailable. Please try again later.');
        }
        throw error;
      }

      setVerificationSent(true);
      setError('');
    } catch (error) {
      console.error('Full error details:', error);
      setError(error.message || 'An unexpected error occurred');
    }
  };


  const handleVerify = async () => {
    try {
      // Use the same formatting logic as handleSignIn
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      let formattedPhoneNumber;
      if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
        formattedPhoneNumber = `+${cleanPhone}`;
      } else if (cleanPhone.length === 10) {
        formattedPhoneNumber = `+1${cleanPhone}`;
      } else {
        formattedPhoneNumber = `+${cleanPhone}`;
      }
      
      const { data: { session }, error } = await supabase.auth.verifyOtp({
        phone: formattedPhoneNumber,
        token: code,
        type: 'sms'
      });

      if (error) {
        throw error;
      }

      if (session) {
        
        // Get the employee data from the employees table
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('employee_number, employee_id_kf, employee_id_koo')
          .eq('phone', formattedPhoneNumber);

        if (employeeError) {
          throw employeeError;
        }

        if (!employeeData || employeeData.length === 0) {
          throw new Error('No employee found with this phone number');
        }

        if (employeeData.length > 1) {
          // Multiple employees with same phone - use the first one
          console.warn(`Multiple employees found with phone ${formattedPhoneNumber}, using first match`);
        }

        const employee = employeeData[0];
        setCurrentEmployee(employee);

        // Check if this is the admin user
        const adminPhone = '+19299906651'; // Admin phone number
        const isAdminUser = formattedPhoneNumber === adminPhone;
        setIsAdmin(isAdminUser);

        if (isAdminUser) {
          // Admin user - just authenticate, don't fetch paystubs
          setIsAuthenticated(true);
          setError('');
          return;
        }

        // Regular user - fetch paystubs (SECURITY: Only for authenticated employee)
        // For staff users, check for payroll period in Processing status
        // Processing means period ended and needs staff confirmation before publishing
        // Current means period is still active (no confirmation needed yet)
        const { data: currentPeriod, error: currentPeriodError } = await supabase
          .from('payroll_period')
          .select('payroll_number, period_label, status')
          .eq('status', 'Processing') // Only Processing status shows confirmation workflow
          .single();
        
        let allPaystubData = [];
        let hasCurrentPayroll = false;

        if (!currentPeriodError && currentPeriod) {
          // Set the current payroll number for confirmation workflow
          setCurrentPayrollNumber(currentPeriod.payroll_number);
          hasCurrentPayroll = true;
        }
        
        // Fetch published payrolls for staff display
        const { data: publishedPeriods, error: periodsError } = await supabase
          .from('payroll_period')
          .select('payroll_number')
          .eq('status', 'Published')
          .order('payroll_number', { ascending: false });

        if (!periodsError && publishedPeriods && publishedPeriods.length > 0) {
          const publishedPayrollNumbers = publishedPeriods.map(p => p.payroll_number);
          
          const { data: publishedPaystubData, error: fetchError } = await supabase
            .from('payroll_summary')
            .select('*')
            .eq('employee_number', employee.employee_number)
            .in('payroll_number', publishedPayrollNumbers)
            .order('payroll_number', { ascending: false });

          if (!fetchError && publishedPaystubData) {
            allPaystubData = publishedPaystubData.slice(0, 2); // Limit to last 2
          }
        }

        // Allow empty paystubs if there's a processing payroll for confirmation
        if (!allPaystubData || allPaystubData.length === 0) {
          if (!hasCurrentPayroll) {
            throw new Error('No paystubs found for this employee');
          }
          allPaystubData = []; // Empty array is OK if we have processing payroll
        }

        const limitedPaystubData = allPaystubData;

        // Fetch deductions for all paystubs using correct employee IDs
        const paystubsWithDeductions = await Promise.all(
          limitedPaystubData.map(async (paystub, index) => {
            let deductions = {};
            
            // Try to fetch deductions using employee_id_kf or employee_id_koo from employees table
            // First check which company this paystub is for and use the corresponding employee_id
            const companyCode = paystub.company;
            let employeeIdToMatch = null;

            if (companyCode === 'KF' && employee.employee_id_kf) {
              employeeIdToMatch = employee.employee_id_kf;
            } else if (companyCode === 'KOO' && employee.employee_id_koo) {
              employeeIdToMatch = employee.employee_id_koo;
            }

            if (employeeIdToMatch) {
              const { data: deductionsData, error: deductionsError } = await supabase
                .from('payroll_dynamics')
                .select('*')
                .eq('employee_id', employeeIdToMatch)
                .eq('company', companyCode)
                .eq('payroll_number', paystub.payroll_number)
                .limit(1);

              if (deductionsError) {
                console.warn('Error fetching deductions for employee_id:', employeeIdToMatch, deductionsError);
                // Set deductions to 0 on error
                deductions = {
                  employee_deductions: 0,
                  employee_taxes: 0,
                  employer_deductions: 0,
                  employer_taxes: 0,
                  net_payment: 0
                };
              } else if (deductionsData && deductionsData.length > 0) {
                // Extract only specific deduction fields, preserve original paystub hours
                const deductionData = deductionsData[0];
                deductions = {
                  employee_deductions: deductionData.employee_deductions,
                  employee_taxes: deductionData.employee_taxes,
                  employer_deductions: deductionData.employer_deductions,
                  employer_taxes: deductionData.employer_taxes,
                  // Preserve original paystub hours (timesheet) and add benefit hours
                  // Total hours = original timesheet hours + benefit hours (PTO, sick leave, lunch)
                  pto_hp: deductionData.pto_hp || paystub.pto_hp || 0,
                  sick_leave: deductionData.sick_leave || paystub.sick_leave || 0,
                  lunch: deductionData.lunch || paystub.lunch || 0,
                  // Add net_payment for Direct Deposit display
                  net_payment: deductionData.net_payment || 0
                };
              } else {
                // No deductions found in payroll_dynamics for this payroll number
                deductions = {
                  employee_deductions: 0,
                  employee_taxes: 0,
                  employer_deductions: 0,
                  employer_taxes: 0,
                  net_payment: 0
                };
              }
            } else {
              console.warn('No matching employee_id found for company:', companyCode);
              // Set deductions to 0 when no employee_id match
              deductions = {
                employee_deductions: 0,
                employee_taxes: 0,
                employer_deductions: 0,
                employer_taxes: 0,
                net_payment: 0
              };
            }

            // For employees with dual company access, aggregate benefit hours from both companies
            let aggregatedBenefitHours = {
              pto_hp: deductions.pto_hp || 0,
              sick_leave: deductions.sick_leave || 0,
              lunch: deductions.lunch || 0
            };

            // If employee has both company IDs, fetch and aggregate benefit hours from the other company
            if (employee.employee_id_kf && employee.employee_id_koo) {
              const otherCompanyCode = companyCode === 'KF' ? 'KOO' : 'KF';
              const otherEmployeeId = companyCode === 'KF' ? employee.employee_id_koo : employee.employee_id_kf;
              
              try {
                const { data: otherCompanyDeductions, error: otherCompanyError } = await supabase
                  .from('payroll_dynamics')
                  .select('pto_hp, sick_leave, lunch')
                  .eq('employee_id', otherEmployeeId)
                  .eq('company', otherCompanyCode)
                  .eq('payroll_number', paystub.payroll_number)
                  .limit(1);

                if (!otherCompanyError && otherCompanyDeductions && otherCompanyDeductions.length > 0) {
                  const otherDeductions = otherCompanyDeductions[0];
                  aggregatedBenefitHours = {
                    pto_hp: (deductions.pto_hp || 0) + (parseFloat(otherDeductions.pto_hp) || 0),
                    sick_leave: (deductions.sick_leave || 0) + (parseFloat(otherDeductions.sick_leave) || 0),
                    lunch: (deductions.lunch || 0) + (parseFloat(otherDeductions.lunch) || 0)
                  };
                }
              } catch (error) {
                console.warn('Error fetching other company benefit hours:', error);
              }
            }

            const finalPaystub = {
              ...paystub,
              ...deductions, // Merge deduction data
              // Override with aggregated benefit hours (but preserve original total_hours for timesheet calculation)
              ...aggregatedBenefitHours,
              // Preserve original total_hours from payroll_summary for this specific company
              total_hours: paystub.total_hours, // Keep original timesheet hours for this company
              // Add default values for display
              pay_date: '2025-06-27', // Friday after June 22, 2025 period end
              pay_period: 'June 9-22, 2025', // Default from the Python script
              display_index: index + 1
            };

            return finalPaystub;
          })
        );

        setPaystubs(paystubsWithDeductions);
        setSelectedPaystub(null); // Start with list view, let user select
        setIsAuthenticated(true);
        setError('');
      } else {
        setError('Invalid verification code.');
      }
    } catch (error) {
      setError(error.message);
      setPaystubs([]);
      setSelectedPaystub(null);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setPhoneNumber('');
    setCode('');
    setPaystubs([]);
    setSelectedPaystub(null);
    setCurrentEmployee(null);
    setVerificationSent(false);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setError('');
    setImpersonationMode(null);
    setAdminSession(null);
  };

  const handleImpersonation = async (employeeData) => {
    try {
      // Store admin session info
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setAdminSession({
          adminPhone: session.user.phone,
          adminEmail: session.user.email,
          adminId: session.user.id
        });
      }

      // Get employee details
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .select('employee_number, employee_id_kf, employee_id_koo, phone')
        .eq('employee_number', employeeData.employeeNumber)
        .single();

      if (employeeError || !employee) {
        setError('Employee not found');
        return;
      }

      // Set impersonation mode
      setImpersonationMode({
        employeeName: employeeData.employeeName,
        employeeNumber: employeeData.employeeNumber
      });

      // Set up as employee without signing out admin
      setCurrentEmployee(employee);
      setIsAdmin(false);
      setPhoneNumber(employee.phone || '');

      // Fetch employee paystubs
      const { data: publishedPeriods } = await supabase
        .from('payroll_period')
        .select('payroll_number')
        .eq('status', 'Published')
        .order('payroll_number', { ascending: false });

      if (publishedPeriods && publishedPeriods.length > 0) {
        const publishedPayrollNumbers = publishedPeriods.map(p => p.payroll_number);
        
        const { data: paystubData } = await supabase
          .from('payroll_summary')
          .select('*')
          .eq('employee_number', employeeData.employeeNumber)
          .in('payroll_number', publishedPayrollNumbers)
          .order('payroll_number', { ascending: false });

        setPaystubs(paystubData || []);
      }

      // Check for current payroll period
      const { data: currentPeriod } = await supabase
        .from('payroll_period')
        .select('payroll_number, period_label, status')
        .eq('status', 'Processing')
        .single();

      if (currentPeriod) {
        setCurrentPayrollNumber(currentPeriod.payroll_number);
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      setError('Failed to impersonate employee');
    }
  };

  const exitImpersonation = () => {
    // Restore admin state
    if (adminSession) {
      setIsAdmin(true);
      setIsAuthenticated(true);
      setPhoneNumber(adminSession.adminPhone);
      setCurrentEmployee(null);
      setPaystubs([]);
      setSelectedPaystub(null);
      setCurrentPayrollNumber(null);
      setImpersonationMode(null);
      setError('');
    }
  };

  return (
    <div className="App min-h-screen">
      <header className="App-header min-h-screen">
        {connectionStatus === 'checking' ? (
          <div className="text-center">
            <h1>Paystub Viewer</h1>
            <p>Connecting to database...</p>
          </div>
        ) : connectionStatus === 'error' ? (
          <div className="text-center">
            <h1>Paystub Viewer</h1>
            <p className="error">Connection Error</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry Connection
            </button>
            {error && <p className="error mt-2">{error}</p>}
          </div>
        ) : !isAuthenticated ? (
          <>
            <h1>Paystub Viewer</h1>
            {!verificationSent ? (
              <div className="input-container">
                <input
                  type="text"
                  placeholder="Enter Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <button onClick={handleSignIn} disabled={connectionStatus !== 'connected'}>
                  {connectionStatus === 'connected' ? 'Sign In' : 'Connecting...'}
                </button>
              </div>
            ) : (
              <div className="input-container">
                <input
                  type="text"
                  placeholder="Enter Verification Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button onClick={handleVerify}>Verify</button>
              </div>
            )}
            {error && <p className="error">{error}</p>}
            {/* Cache clear button for troubleshooting */}
            <div className="mt-4">
              <button 
                onClick={clearAllCaches}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
                title="Clear all cached data and refresh the app"
              >
                Clear Cache & Refresh
              </button>
            </div>
          </>
        ) : isAdmin && !impersonationMode ? (
          <AdminDashboard onLogout={handleLogout} onImpersonate={handleImpersonation} />
        ) : (
          <>
            {/* Impersonation Mode Banner */}
            {impersonationMode && (
              <div className="bg-purple-600 text-white px-4 py-2 text-center">
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>
                    Admin viewing as: {impersonationMode.employeeName} 
                    ({impersonationMode.employeeNumber})
                  </span>
                  <button
                    onClick={exitImpersonation}
                    className="ml-4 px-2 py-1 bg-purple-800 rounded hover:bg-purple-900 text-sm"
                  >
                    Exit Impersonation
                  </button>
                </div>
              </div>
            )}
            <div className="paystub-container w-full px-0 sm:px-4">
            {selectedPaystub ? (
              <>
                <div className="mb-4">
                  <button
                    onClick={() => setSelectedPaystub(null)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors flex items-center gap-2"
                  >
                    ← Back to List
                  </button>
                </div>
                <PaystubViewer paystubData={selectedPaystub} />
              </>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Pay Rate Card (only for authenticated staff, not admins) */}
                {!isAdmin && currentEmployee && (
                  <PayRateCard
                    employeeData={currentEmployee}
                    onRequestAdjustment={async () => {
                      // Fetch current rates before opening modal
                      try {
                        const { data: ratesData, error: ratesError } = await supabase
                          .from('employee_pay_rates')
                          .select('*')
                          .eq('employee_number', currentEmployee.employee_number)
                          .is('end_date', null);
                        
                        if (!ratesError && ratesData) {
                          setCurrentRates(ratesData);
                        }

                        // Check for rejected requests
                        const { data: rejectedData, error: rejectedError } = await supabase
                          .from('pay_rate_adjustment_requests')
                          .select('*')
                          .eq('employee_number', currentEmployee.employee_number)
                          .eq('status', 'rejected')
                          .order('submitted_at', { ascending: false })
                          .limit(1);
                        
                        if (!rejectedError && rejectedData && rejectedData.length > 0) {
                          setRejectedRequest(rejectedData[0]);
                        } else {
                          setRejectedRequest(null);
                        }
                      } catch (error) {
                        console.error('Error fetching rates:', error);
                        setRejectedRequest(null);
                      }
                      setShowRateAdjustmentModal(true);
                    }}
                  />
                )}

                {/* My Rate Requests (only for authenticated staff, not admins) */}
                {!isAdmin && currentEmployee && (
                  <MyRateRequests employeeData={currentEmployee} />
                )}

                {/* Processing Payroll Confirmation (only for payrolls in Processing status) */}
                {currentEmployee && currentPayrollNumber && (
                  <CurrentPayrollConfirmation
                    employeeData={currentEmployee}
                    currentPayrollNumber={currentPayrollNumber}
                  />
                )}
                
                {/* Published Payroll History */}
                <PaystubPeriodList
                  paystubs={paystubs}
                  selectedPaystub={null}
                  onSelect={setSelectedPaystub}
                  onLogout={handleLogout}
                />
              </div>
            )}
          </div>
          </>
        )}
      </header>

      {/* Rate Adjustment Modal */}
      {!isAdmin && showRateAdjustmentModal && currentEmployee && (
        <RateAdjustmentModal
          isOpen={showRateAdjustmentModal}
          onClose={() => {
            setShowRateAdjustmentModal(false);
            setRejectedRequest(null);
          }}
          employeeData={currentEmployee}
          currentRates={currentRates}
          existingRequest={rejectedRequest}
          onSubmitSuccess={() => {
            // Refresh the page or show success message
            setShowRateAdjustmentModal(false);
            setRejectedRequest(null);
          }}
        />
      )}
    </div>
  );
}

export default App;
// Cache bust: Tue Jul 22 10:58:03 EDT 2025
